import { ReactNode } from 'react';
import { isAxiosError } from 'axios';
import { Link } from '~/commonComponents';

const POPUP_CONFIGS: Record<PopupType, PopupConfig> = {
  psa: {
    title: 'Personnel Service Agreement',
    description:
      "Before posting a shift, please take a moment to review and accept GoTu's Personnel Service Agreement for the office you're posting on behalf of.",
    type: 'psa',
    links: [
      {
        text: "GoTu's Personnel Service Agreement",
        href: 'https://joingotu.com/w-2-psa/',
      },
    ],
  },
  gotuTerms: {
    title: 'Terms and Conditions',
    description:
      "Before posting a shift, please take a moment to review and accept GoTu's Terms and Conditions for the office you're posting on behalf of.",
    type: 'gotuTerms',
    links: [
      {
        text: "GoTu's Terms and Conditions",
        href: 'https://joingotu.com/client-terms-of-use/',
      },
    ],
  },
  both: {
    title: 'GoTu Policies',
    description:
      "Before posting a shift, please take a moment to review and accept GoTu's Policies for the office you're posting on behalf of.",
    type: 'both',
    links: [
      {
        text: "GoTu's Terms and Conditions",
        href: 'https://joingotu.com/client-terms-of-use/',
      },
      {
        text: 'Personnel Service Agreement',
        href: 'https://joingotu.com/w-2-psa/',
      },
    ],
  },
};

type PopupType = 'psa' | 'gotuTerms' | 'both';

interface PopupConfig {
  title: string;
  description: string;
  type: PopupType;
  links: { text: string; href: string }[];
}

const getPopupType = (isTCMissing: boolean, isPSAMissing: boolean): PopupType | null => {
  if (isTCMissing && isPSAMissing) return 'both';
  if (isTCMissing) return 'gotuTerms';
  if (isPSAMissing) return 'psa';

  return null;
};

/**
 * The terms endpoints refuse a second accept with 400 "PSA terms already accepted" / "Gotu terms already
 * accepted" (tempmee_server `ErrorMessages`). The office has agreed either way, so the popup takes it as
 * done: otherwise a half-accepted 'both' popup, or one opened from a record read before another session
 * agreed, fails on every retry and cannot be closed.
 */
const isTermsAlreadyAcceptedError = (error: unknown): boolean =>
  isAxiosError<{ message?: string }>(error) &&
  error.response?.status === 400 &&
  /terms already accepted/i.test(error.response.data?.message ?? '');

const renderSwitchText = (type: PopupType, links: { text: string; href: string }[]): ReactNode => {
  if (type === 'both') {
    const [firstLink, secondLink] = links;
    if (firstLink && secondLink) {
      return (
        <>
          I agree to <Link text={firstLink.text} href={firstLink.href} /> and{' '}
          <Link text={secondLink.text} href={secondLink.href} />
        </>
      );
    }

    return <>I agree to</>;
  }

  const firstLink = links[0];
  if (firstLink) {
    return (
      <>
        I agree to <Link text={firstLink.text} href={firstLink.href} />
      </>
    );
  }

  return <>I agree to</>;
};
export { getPopupType, isTermsAlreadyAcceptedError, POPUP_CONFIGS, renderSwitchText };
